import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NoteAltOutlinedIcon from '@mui/icons-material/NoteAltOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import { GetNotes, CreateNote, DeleteNote } from '../../redux/actions/noteActions';
import { CLEAR_NOTE_LIMIT } from '../../redux/actionTypes';

import "./styles.scss";

// ── helpers ──────────────────────────────────────────────────────────────────
const extractSnippet = (body) => {
  if (!body?.blocks?.length) return '';
  const text = body.blocks
    .filter(b => b.data?.text)
    .map(b => b.data.text.replace(/<[^>]+>/g, ''))
    .join(' ');
  return text.length > 110 ? text.slice(0, 110) + '…' : text;
};

const countWords = (body) => {
  if (!body?.blocks?.length) return 0;
  const text = body.blocks
    .filter(b => b.data?.text)
    .map(b => b.data.text.replace(/<[^>]+>/g, ''))
    .join(' ');
  return text.trim() ? text.trim().split(/\s+/).length : 0;
};

// ── Skeleton card ─────────────────────────────────────────────────────────────
const NoteCardSkeleton = () => (
  <Box className="noteCard noteCard--skeleton flex_FSColumn">
    <Box className="flexCenterSBRow" mb={1}>
      <Skeleton variant="text" width="65%" height={20} />
      <Skeleton variant="circular" width={24} height={24} />
    </Box>
    <Skeleton variant="text" width="100%" height={16} />
    <Skeleton variant="text" width="88%"  height={16} />
    <Box className="flexCenterSBRow" sx={{ mt: 'auto', pt: 1.5 }}>
      <Skeleton variant="text" width={48} height={14} />
      <Skeleton variant="text" width={72} height={14} />
    </Box>
  </Box>
);

// ── Component ─────────────────────────────────────────────────────────────────
const HomePage = () => {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const notesList  = useSelector(state => state.notes.notes);
  const noteLimitReached = useSelector(state => state.notes.noteLimitReached);
  const maxNotes        = useSelector(state => state.notes.maxNotes ?? 3);
  const user       = useSelector(state => state.auth.user);
  const paymentFailed   = user?.subscription?.payment_failed ?? false;
  const paymentFailureReason = user?.subscription?.payment_failure_reason;

  const [greeting, setGreeting] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ open: false, noteId: null });
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    handleGreeting();
    dispatch(GetNotes()).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateNewNote = async () => {
    if (creating) return;
    setCreating(true);
    try {
      await dispatch(CreateNote(navigate));
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  };

  const handleOpenNote      = (id) => navigate(`/app/${id}`);

  // Opens the confirmation dialog instead of deleting immediately
  const requestDeleteNote = (e, id) => {
    e.stopPropagation();
    setDeleteModal({ open: true, noteId: id });
  };

  const confirmDeleteNote = async () => {
    setDeleting(true);
    try {
      await dispatch(DeleteNote(deleteModal.noteId, null));
    } finally {
      if (mountedRef.current) {
        setDeleting(false);
        setDeleteModal({ open: false, noteId: null });
      }
    }
  };

  const cancelDeleteNote = () => {
    setDeleteModal({ open: false, noteId: null });
  };

  const handleGreeting = () => {
    const h = new Date().getHours();
    if (h >= 5  && h < 12) setGreeting('Good Morning');
    else if (h >= 12 && h < 17) setGreeting('Good Afternoon');
    else setGreeting('Good Evening');
  };

  const firstName = user?.name?.split(' ')[0] ?? '';

  return (
    <>
      <Helmet>
        <title>My Notes — NoteCORE</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Container maxWidth="lg" sx={{ py: 3, px: { xs: 2, sm: 3 } }}>

      {/* ── Payment Failed Banner ── */}
      {paymentFailed && (
        <Box
          sx={{
            mb: 3,
            px: 2.5,
            py: 1.75,
            borderRadius: '14px',
            bgcolor: 'rgba(239, 68, 68, 0.07)',
            border: '1.5px solid rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <WarningAmberRoundedIcon sx={{ color: '#dc2626', fontSize: 20, flexShrink: 0 }} />
          <Box flex={1}>
            <Typography variant="body2" fontWeight={700} color="error.main">
              There was a problem with your payment
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {paymentFailureReason || 'Your subscription payment failed. Please update your payment method to keep access.'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => navigate('/pricing')}
            sx={{ borderRadius: '10px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            Fix payment
          </Button>
        </Box>
      )}

      {/* ── Header ── */}
      <Box
        className="flexCenterSBRow"
        mb={3}
        sx={{ flexWrap: 'wrap', gap: 1.5 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {greeting}{firstName ? `, ${firstName}` : ''} 👋
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {loading
              ? 'Loading your notes…'
              : notesList.length
                ? `${notesList.length} note${notesList.length !== 1 ? 's' : ''} in your collection`
                : 'Start building your note collection'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={creating ? null : <AddRoundedIcon />}
          onClick={handleCreateNewNote}
          disabled={creating}
          sx={{ borderRadius: '14px', px: 2.5, py: 1, fontWeight: 600, minWidth: 130 }}
        >
          {creating
            ? <><CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />Creating…</>
            : 'New Note'}
        </Button>
      </Box>

      {/* ── Grid ── */}
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} lg={4} key={i}>
              <NoteCardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : notesList.length === 0 ? (
        <Box
          className="flexCenterCenterColumn"
          sx={{ py: 12, gap: 2, textAlign: 'center' }}
        >
          <NoteAltOutlinedIcon
            sx={{ fontSize: 60, color: 'primary.light', opacity: 0.6 }}
          />
          <Typography variant="h6" fontWeight={600}>
            No notes yet
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', maxWidth: 280 }}
          >
            Hit "New Note" to create your first note and get started.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {notesList.map((note, index) => (
            <Grid item xs={12} sm={6} lg={4} key={note.id || index}>
              <Box
                className="noteCard flex_FSColumn"
                style={{ animationDelay: `${index * 0.06}s` }}
                onClick={() => handleOpenNote(note.id)}
              >
                {/* title row */}
                <Box className="flexCenterSBRow" mb={1}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 'calc(100% - 36px)',
                    }}
                  >
                    {note.title || 'Untitled'}
                  </Typography>
                  <Tooltip title="Delete note">
                    <IconButton
                      size="small"
                      onClick={(e) => requestDeleteNote(e, note.id)}
                      sx={{
                        color: 'text.disabled',
                        transition: 'color 0.2s',
                        '&:hover': { color: 'error.main' },
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* snippet */}
                <Typography
                  variant="body2"
                  className="note-snippet"
                  sx={{ color: 'text.secondary', flexGrow: 1, lineHeight: 1.65 }}
                >
                  {extractSnippet(note.body) || <em style={{ opacity: 0.5 }}>Empty note</em>}
                </Typography>

                {/* footer */}
                <Box className="flexCenterSBRow" sx={{ mt: 'auto', pt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {countWords(note.body)}&nbsp;words
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {new Date(note.updated_at).toLocaleDateString()}&nbsp;·&nbsp;
                    {new Date(note.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteModal.open}
        onClose={cancelDeleteNote}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 24 }} />
          <Typography variant="h6" fontWeight={700} component="span">
            Delete Note?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary', fontSize: '14px', lineHeight: 1.65 }}>
            This note will be <strong>permanently deleted</strong> and cannot be recovered.
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={cancelDeleteNote}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDeleteNote}
            disabled={deleting}
            sx={{
              flex: 1,
              backgroundColor: 'error.main',
              '&:hover': {
                backgroundColor: '#dc2626',
                boxShadow: '0 6px 20px rgba(239,68,68,0.4)',
              },
            }}
          >
            {deleting
              ? <CircularProgress size={16} color="inherit" />
              : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Note Limit Upgrade Dialog ── */}
      <Dialog
        open={noteLimitReached}
        onClose={() => dispatch({ type: CLEAR_NOTE_LIMIT })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <AutoAwesomeRoundedIcon sx={{ color: 'primary.main', fontSize: 24 }} />
          <Typography variant="h6" fontWeight={700} component="span">
            You've hit your limit
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary', fontSize: '14px', lineHeight: 1.65 }}>
            You've reached your limit of <strong>{maxNotes} note{maxNotes !== 1 ? 's' : ''}</strong> on the free plan.
            Upgrade to create unlimited notes and unlock more features.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => dispatch({ type: CLEAR_NOTE_LIMIT })}
            sx={{ flex: 1 }}
          >
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              dispatch({ type: CLEAR_NOTE_LIMIT });
              navigate('/pricing');
            }}
            sx={{
              flex: 1,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
              '&:hover': { background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)' },
            }}
          >
            See plans
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
    </>
  );
};

export default HomePage;